import { supabase } from '../supabaseClient';

/* ══════════════════════════════════════════════════════════════════════════
   OLVASÓ, 4. KÖR — kitölthető lapok (a prototípus szerint:
   docs/weboldal-2026-10/olvaso/kor4/kitoltes_prototipus.html a landing repóban)
   ─────────────────────────────────────────────────────────────────────────
   Szerver (landing migráció 20261127120000_olvaso_4): olvaso_kitoltes(slug, peldany),
   olvaso_kitoltes_mentes(slug, peldany, kulcs, ertek), olvaso_peldanyok(slug),
   olvaso_peldany_torles(slug, peldany), olvaso_kitoltes_lista(), olvaso_kitoltes_hiba(slug, kulcs, kod).
   A lap mezői a HTML-ben jelöltek (data-olv-t: c, r, t, ta, tbl); a hydrate() teszi őket
   interaktívvá a Shadow DOM-ban. A mentés mezőnként késleltetett (600 ms), hálózati hibánál a
   változás a memóriában vár, és újrapróbálja. A kitöltés személyes adat: érték soha nem kerül
   naplóba vagy eseménybe; a hibanaplóba csak a slug, a kulcs és a hibakód.
   ══════════════════════════════════════════════════════════════════════════ */

const rpc = async (name, args) => {
  const { data, error } = await supabase.rpc(name, args);
  if (error) throw error;
  return data;
};

export const KITOLTES_API = {
  ertekek: (slug, peldany) => rpc('olvaso_kitoltes', { p_slug: slug, p_peldany: peldany }),
  mentes: (slug, peldany, kulcs, ertek) => rpc('olvaso_kitoltes_mentes', { p_slug: slug, p_peldany: peldany, p_kulcs: kulcs, p_ertek: ertek }),
  peldanyok: (slug) => rpc('olvaso_peldanyok', { p_slug: slug }),
  torles: (slug, peldany) => rpc('olvaso_peldany_torles', { p_slug: slug, p_peldany: peldany }),
  hiba: (slug, kulcs, kod) => supabase.rpc('olvaso_kitoltes_hiba', { p_slug: slug, p_kulcs: kulcs, p_kod: kod })
    .then(() => undefined, () => undefined),
};

/* A könyvtár és a modulkártya sora: anyagonként a saját kitöltés összegzése (egyszer oldalbetöltésenként). */
let listaPromise = null;
export function kitoltesListaCached() {
  if (!listaPromise) {
    listaPromise = rpc('olvaso_kitoltes_lista', {}).then(rows => Object.fromEntries((rows || []).map(r => [r.slug, r])))
      .catch(() => { listaPromise = null; return {}; });
  }
  return listaPromise;
}

/* ── Példányok ── */

const HONAPOK = ['január', 'február', 'március', 'április', 'május', 'június', 'július', 'augusztus', 'szeptember', 'október', 'november', 'december'];
const pad = (n) => String(n).padStart(2, '0');

/** A mai nap Europe/Budapest szerint: [év, hónap (1–12), nap]. */
export function budapestDate(now = new Date()) {
  const s = new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/Budapest', year: 'numeric', month: '2-digit', day: '2-digit' }).format(now);
  return s.split('-').map(Number);
}

/** ISO-hét: [év, hét] egy (UTC-ként kezelt) naptári napra. */
export function isoWeek(y, m, d) {
  const t = new Date(Date.UTC(y, m - 1, d));
  const day = t.getUTCDay() || 7;
  t.setUTCDate(t.getUTCDate() + 4 - day);
  const y1 = new Date(Date.UTC(t.getUTCFullYear(), 0, 1));
  return [t.getUTCFullYear(), Math.ceil(((t - y1) / 864e5 + 1) / 7)];
}

/** Az ISO-hét hétfője (UTC-dátum). */
function weekMonday(y, w) {
  const jan4 = new Date(Date.UTC(y, 0, 4));
  const mon = new Date(jan4);
  mon.setUTCDate(jan4.getUTCDate() - ((jan4.getUTCDay() || 7) - 1) + (w - 1) * 7);
  return mon;
}

export const parseTerv = (p) => (/^terv-(\d+)$/.test(p || '') ? Number(p.slice(5)) : null);

/** Az alapértelmezett példány: az aktuális hét vagy hónap (Europe/Budapest), a legutóbb frissített terv, vagy „1”. */
export function defaultPeldany(mod, peldanyok = [], now = new Date()) {
  const [y, m, d] = budapestDate(now);
  if (mod === 'heti') { const [wy, w] = isoWeek(y, m, d); return `${wy}-W${pad(w)}`; }
  if (mod === 'havi') return `${y}-${pad(m)}`;
  if (mod === 'tobbszoros') return peldanyok[0]?.peldany || 'terv-1';
  return '1';
}

/** Egy héttel vagy hónappal arrébb (dir = ±1). */
export function shiftPeldany(mod, p, dir) {
  if (mod === 'heti') {
    const [y, w] = p.split('-W').map(Number);
    const mon = weekMonday(y, w);
    mon.setUTCDate(mon.getUTCDate() + 7 * dir);
    const [ny, nw] = isoWeek(mon.getUTCFullYear(), mon.getUTCMonth() + 1, mon.getUTCDate());
    return `${ny}-W${pad(nw)}`;
  }
  if (mod === 'havi') {
    let [y, m] = p.split('-').map(Number);
    m += dir;
    if (m < 1) { y -= 1; m = 12; }
    if (m > 12) { y += 1; m = 1; }
    return `${y}-${pad(m)}`;
  }
  return p;
}

/** A felső sáv felirata: „2026. 44. hét”, „2026. október”, a terv neve, vagy üres. */
export function peldanyLabel(mod, p, nev) {
  if (mod === 'heti') { const [y, w] = p.split('-W'); return `${y}. ${Number(w)}. hét`; }
  if (mod === 'havi') { const [y, m] = p.split('-'); return `${y}. ${HONAPOK[Number(m) - 1]}`; }
  if (mod === 'tobbszoros') return nev || `${parseTerv(p)}. terv`;
  return '';
}

/** Az új terv sorszáma: a legnagyobb meglévő + 1 (legfeljebb 200); null, ha nincs több hely. */
export function ujTerv(peldanyok, aktualis) {
  const n = Math.max(0, ...[...(peldanyok || []).map(x => parseTerv(x.peldany)), parseTerv(aktualis)].filter(Number.isInteger));
  return n >= 200 ? null : `terv-${n + 1}`;
}

/** A könyvtár és a modulkártya rövid sora: „12 / 24 kipipálva”, „3 terv”, „utoljára: 44. hét”. Üres, ha nincs kitöltés. */
export function kitoltesSor(r) {
  if (!r || !r.peldany_db) return null;
  if (r.mod === 'tobbszoros') return `${r.peldany_db} terv`;
  if (r.mod === 'heti' && r.utolso_peldany) return `utoljára: ${Number(r.utolso_peldany.split('-W')[1])}. hét`;
  if (r.mod === 'havi' && r.utolso_peldany) return `utoljára: ${HONAPOK[Number(r.utolso_peldany.split('-')[1]) - 1]}`;
  if (r.mod === 'egyszeri') {
    if (r.pipa_ossz > 0) return `${r.pipa} / ${r.pipa_ossz} kipipálva`;
    return `${r.kitoltott} / ${r.mezo_ossz} mező kitöltve`;
  }
  return null;
}

/* ── Mentés ── */

/** A szerver hibakódjai közül ezeknél érdemes később újra próbálni (a többi végleges: az érték nem jó). */
const UJRA = new Set(['korlat']);

/**
 * Mezőnkénti késleltetett mentés. set(peldany, kulcs, ertek): a mező utolsó értéke `delay` ms múlva megy ki
 * (ugyanazon mező újabb változása újraindítja az órát). Hálózati hibánál (vagy korlátnál) a változás a memóriában
 * vár, és `retryMs` múlva, illetve az `online` eseményre újra próbálja. Állapot (onStatus): 'mentes' | 'mentve' |
 * 'hiba' | null. onSaved(peldany, kulcs, res) a sikeres mentés után; onReject(peldany, kulcs, kod) a végleges
 * elutasításnál; onNetError(kulcs) egy hibasorozat első hibájánál.
 */
export function createSaver({ save, onStatus, onSaved, onReject, onNetError, delay = 600, retryMs = 5000, win = typeof window !== 'undefined' ? window : null }) {
  const pending = new Map();      // "peldany\u0000kulcs" → { peldany, kulcs, ertek }
  const timers = new Map();
  let inflight = 0;
  let failed = false;
  let retryTimer = null;
  let disposed = false;
  const status = (s) => { if (!disposed) onStatus?.(s); };
  const keyOf = (p, k) => `${p}\u0000${k}`;

  async function send(key) {
    const item = pending.get(key);
    if (!item) return;
    pending.delete(key);
    inflight += 1;
    status('mentes');
    try {
      const res = await save(item.peldany, item.kulcs, item.ertek);
      if (res?.hiba && !UJRA.has(res.hiba)) {
        onReject?.(item.peldany, item.kulcs, res.hiba);
        failed = true;
      } else if (res?.hiba) {
        if (!pending.has(key)) pending.set(key, item);
        failed = true;
      } else {
        onSaved?.(item.peldany, item.kulcs, res);
      }
    } catch (e) {
      if (!pending.has(key)) pending.set(key, item);      // a közben jött újabb érték az erősebb
      if (!failed) onNetError?.(item.kulcs);
      failed = true;
    } finally {
      inflight -= 1;
    }
    settle();
  }

  function settle() {
    if (inflight > 0 || timers.size > 0) return;
    if (pending.size > 0) {
      status('hiba');
      if (!retryTimer) retryTimer = setTimeout(() => { retryTimer = null; retry(); }, retryMs);
    } else {
      status(failed ? 'hiba' : 'mentve');
    }
  }

  function set(peldany, kulcs, ertek) {
    const key = keyOf(peldany, kulcs);
    pending.set(key, { peldany, kulcs, ertek });
    clearTimeout(timers.get(key));
    timers.set(key, setTimeout(() => { timers.delete(key); send(key); }, delay));
    status('mentes');
  }

  function flushNow() {
    for (const [key, t] of timers) { clearTimeout(t); timers.delete(key); }
    return Promise.all([...pending.keys()].map(send));
  }

  function retry() {
    clearTimeout(retryTimer); retryTimer = null;
    failed = false;
    if (pending.size === 0) { status('mentve'); return Promise.resolve(); }
    return flushNow();
  }

  const onOnline = () => { if (pending.size) retry(); };
  win?.addEventListener?.('online', onOnline);

  return {
    set, flushNow, retry,
    pendingCount: () => pending.size + inflight,
    dispose() { disposed = true; clearTimeout(retryTimer); win?.removeEventListener?.('online', onOnline); },
  };
}

/* ── A lap mezői ── */

const esc = (s) => String(s ?? '').replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;');

/** Üres-e az érték (ilyenkor a mező törlődik a szerveren: null). */
export function uresErtek(v) {
  if (v == null || v === false || v === '') return true;
  if (Array.isArray(v)) return v.every(r => Array.isArray(r) && r.every(c => !c));
  return false;
}

/** Az elem egy kitölthető mezőben van-e (input, textarea, select). */
export const isFieldEl = (el) => Boolean(el && /^(INPUT|TEXTAREA|SELECT)$/.test(el.tagName || ''));

/**
 * A Shadow DOM-ba töltött lap jelölt elemei interaktívvá válnak (a prototípus hydrate()-je szerint).
 * root: a lap gyökere; values: { kulcs: érték } (olvasásra); onChange(kulcs, ertek | null);
 * narrow: keskeny lap (soronként kártya a táblázatnál); onLink(path): a data-olv-link sor gombja.
 */
export function hydrate(root, { values = {}, onChange, narrow = false, onLink } = {}) {
  const change = (k, v) => onChange?.(k, uresErtek(v) ? null : v);

  root.querySelectorAll('[data-olv-t="c"]').forEach(el => {
    const k = el.getAttribute('data-olv-k');
    if (values[k] === true) el.setAttribute('data-on', '');
    const host = el.closest('li') || el.parentElement || el;
    host.style.cursor = 'pointer';
    host.setAttribute('role', 'checkbox');
    host.setAttribute('aria-checked', values[k] === true ? 'true' : 'false');
    host.setAttribute('tabindex', '0');
    const toggle = () => {
      const on = !el.hasAttribute('data-on');
      el.toggleAttribute('data-on', on);
      host.setAttribute('aria-checked', on ? 'true' : 'false');
      change(k, on);
    };
    host.addEventListener('click', toggle);
    host.addEventListener('keydown', (e) => { if (e.key === ' ' || e.key === 'Enter') { e.preventDefault(); toggle(); } });
  });

  root.querySelectorAll('[data-olv-g]').forEach(el => {
    const k = el.getAttribute('data-olv-g');
    const v = el.getAttribute('data-olv-v');
    if (values[k] === v) el.setAttribute('data-on', '');
    el.setAttribute('role', 'radio');
    el.setAttribute('aria-checked', values[k] === v ? 'true' : 'false');
    el.addEventListener('click', (e) => {
      e.stopPropagation();
      const was = el.hasAttribute('data-on');
      const next = was ? null : v;                         // a második koppintás visszavonja
      root.querySelectorAll('[data-olv-g]').forEach(o => {
        if (o.getAttribute('data-olv-g') !== k) return;
        const on = next !== null && o.getAttribute('data-olv-v') === next;
        o.toggleAttribute('data-on', on);
        o.setAttribute('aria-checked', on ? 'true' : 'false');
      });
      change(k, next);
    });
  });

  root.querySelectorAll('[data-olv-t="t"]').forEach(el => {
    const k = el.getAttribute('data-olv-k');
    const inp = root.ownerDocument.createElement('input');
    inp.className = 'olv-in';
    inp.size = 4;
    inp.maxLength = 200;
    inp.value = typeof values[k] === 'string' ? values[k] : '';
    inp.setAttribute('data-olv-k', k);
    inp.setAttribute('aria-label', 'Beírás');
    inp.addEventListener('input', () => change(k, inp.value));
    el.replaceWith(inp);
  });

  root.querySelectorAll('[data-olv-x]').forEach(el => { el.style.display = 'none'; });
  root.querySelectorAll('[data-olv-t="ta"]').forEach(el => {
    const k = el.getAttribute('data-olv-k');
    const ta = root.ownerDocument.createElement('textarea');
    ta.className = 'olv-ta';
    ta.rows = Math.max(2, Number(el.getAttribute('data-olv-sorok')) || 2);
    ta.maxLength = 2000;
    ta.value = typeof values[k] === 'string' ? values[k] : '';
    ta.setAttribute('data-olv-k', k);
    ta.setAttribute('aria-label', 'Beírás');
    ta.addEventListener('input', () => change(k, ta.value));
    el.replaceWith(ta);
  });

  root.querySelectorAll('[data-olv-t="tbl"]').forEach(el => {
    const k = el.getAttribute('data-olv-k');
    let cols;
    try { cols = JSON.parse(el.getAttribute('data-olv-oszlopok') || '[]'); } catch { cols = []; }
    const n = Number(el.getAttribute('data-olv-sorok')) || 1;
    const saved = Array.isArray(values[k]) ? values[k].map(r => cols.map((_, j) => (typeof r?.[j] === 'string' ? r[j] : ''))) : [];
    const rows = saved.length ? saved : Array.from({ length: n }, () => cols.map(() => ''));
    while (rows.length < n) rows.push(cols.map(() => ''));
    const box = root.ownerDocument.createElement('div');
    box.className = 'olv-tbl';
    box.setAttribute('data-olv-k', k);
    const cell = (i, j) => `<input class="olv-in" size="4" maxlength="200" data-r="${i}" data-c="${j}" aria-label="${esc(`${i + 1}. sor, ${cols[j]}`)}" value="${esc(rows[i][j])}">`;
    const draw = () => {
      box.innerHTML = (narrow
        ? rows.map((r, i) => `<div class="row"><b>${i + 1}. sor</b>${cols.map((c, j) => `<label><span>${esc(c)}</span>${cell(i, j)}</label>`).join('')}</div>`).join('')
        : `<table><tr>${cols.map(c => `<th>${esc(c)}</th>`).join('')}</tr>${rows.map((r, i) => `<tr>${cols.map((c, j) => `<td>${cell(i, j)}</td>`).join('')}</tr>`).join('')}</table>`)
        + (rows.length < 20 ? '<button type="button" class="add">+ Új sor</button>' : '');
      box.querySelectorAll('input').forEach(inp => inp.addEventListener('input', () => {
        rows[Number(inp.getAttribute('data-r'))][Number(inp.getAttribute('data-c'))] = inp.value;
        change(k, rows.map(r => [...r]));
      }));
      const add = box.querySelector('.add');
      if (add) add.addEventListener('click', () => {
        rows.push(cols.map(() => ''));
        draw();
        box.querySelectorAll(`input[data-r="${rows.length - 1}"]`)[0]?.focus();
      });
    };
    draw();
    el.replaceWith(box);
  });

  root.querySelectorAll('[data-olv-link]').forEach(el => {
    const path = el.getAttribute('data-olv-link');
    const b = root.ownerDocument.createElement('button');
    b.type = 'button';
    b.className = 'olv-link';
    b.textContent = 'Megnyitom az Alakzatfelismerőt →';
    b.addEventListener('click', () => onLink?.(path));
    el.appendChild(b);
  });
}

/** A kitöltéshez a Shadow DOM-ba, az anyag stílusa és a kitöltés stílusa (olvaso_kitoltes.css) után. */
export const KITOLTES_EXTRA_CSS = '.olv-in,.olv-ta,.olv-tbl input{-webkit-user-select:text;user-select:text}'
  + '[role=checkbox]:focus-visible,[role=radio]:focus-visible{outline:1.5px solid var(--accent);outline-offset:1px}'
  + '.olv-link{display:block;margin-top:2mm;background:var(--accent);color:#04201F;border:0;border-radius:1.6mm;padding:1.6mm 3mm;font:inherit;font-weight:700;font-size:9pt;cursor:pointer}';
